import { atom } from "recoil";
import cookie from 'js-cookie'
import { selectorWithoutCache as selector } from "util/RecoilWithoutCache";
import LocalCache, { CacheKey } from "util/LocalCache";
import LocalCookie, { CookieKey } from "util/LocalCookie";
import { SUPPORT_LAYOUT } from "../../constants/layout";
import { BusinessLineEnum, supportLanguageList } from "../../constants/base";
import { SUPPORTED_LANG, TradeUnitEnum } from "constants/vars";
import { ratesType } from "../../api/common";
import { LanguageTypeItem } from "api/common/model";
import { THEME, WindiMap } from "../../constants/colors";
import { isBrowserEnv } from "@app/utils/constants";
import { ssrUserCookiesSelector } from "./ssrUserContext";

/**
 * 当前语言
 */
export const LocaleState = atom<string>({
  key: "locale-state-inner",
  default: selector({
    key: "locale-state-inner/default",
    get: ({ get }) => {
      let lang =
        (get(ssrUserCookiesSelector(CookieKey.LANG)) as string) || "en";
      if (SUPPORTED_LANG.indexOf(lang) === -1) {
        lang = "en";
      }
      if (isBrowserEnv) {
        if (lang === "sk") {
          document.getElementsByTagName("html")[0].classList.add("sk");
        } else {
          document.getElementsByTagName("html")[0].classList.remove("sk");
        }
      }
      return lang;
    },
  }),
});

/**
 * 当前正在使用的多语言
 * 使用selector主要是为了在set过程中同时写入cookie
 */
export const LocaleSelector = selector<string>({
  key: "locale-selector-public",
  get: ({ get }) => get(LocaleState),
  set: ({ set }, locale = "en") => {
    LocalCookie.set(CookieKey.LANG, locale);
    set(LocaleState, locale);
    // if (locale === "ct") {
    //   document.getElementsByTagName("html")[0].classList.add("ct");
    // } else {
    //   document.getElementsByTagName("html")[0].classList.remove("ct");
    // }
  },
});

// 注意：这里是一个临时用转换主题名称的map
const TempThemeMap = {
  black: THEME.DARK,
  white: THEME.LIGHT,
  dark: THEME.DARK,
  light: THEME.LIGHT,
};

const ThemeState = atom<string>({
  key: "theme-state-inner",
  default: selector({
    key: "theme-state-inner/default",
    get: ({ get }) => {
      let cacheTheme: string = "";

      cacheTheme = LocalCache.get(CacheKey.GLOBAL_THEME) || cookie.get('theme') || THEME.DARK;

      let theme: string =
      cacheTheme in TempThemeMap ? TempThemeMap[cacheTheme] : cacheTheme;
      if (isBrowserEnv) {
        const bodyClassList = document.body.classList;
        if (!bodyClassList.contains(theme)) {
          bodyClassList.add(theme);
          document.documentElement.classList.add(WindiMap[theme]);
        }
      }
      return theme;
    },
  }),
});

/**
 * 当前正在使用的主题
 * 更新时需要写入localStorage
 */
export const ThemeSelector = selector<string>({
  key: "theme-selector-public",
  get: ({ get }) => get(ThemeState),
  set: ({ set, get }, theme) => {
    let oldTheme = get(ThemeState);
    const bodyClassList = document.body.classList;
    if (bodyClassList.contains(oldTheme)) {
      bodyClassList.remove(oldTheme);
    }
    set(ThemeState, theme);
    LocalCache.set(CacheKey.GLOBAL_THEME, theme);
    LocalCookie.set(CookieKey.THEME, theme);
    LocalCache.set(CacheKey.THEME, theme);
    let newTheme = theme as string;
    if (!bodyClassList.contains(newTheme)) {
      bodyClassList.add(newTheme);
    }
    // windicss darkMode
    if (theme === THEME.DARK || theme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  },
});

export const isDarkThemeSelector = selector<boolean>({
  key: "is-dart-theme-selector",
  get: ({ get }) => get(ThemeSelector) === THEME.DARK,
});

/**
 * header中资产是否展示
 */
export const AssetShowState = atom<boolean>({
  key: "asset-show-state",
  default: LocalCookie.get(CookieKey.SHOWASSET),
});

/***
 * 资产是否展示 选择器
 * 需要操作cookie
 */
export const AssetShowSelector = selector<boolean>({
  key: "asset-show-selector",
  get: ({ get }) => get(AssetShowState),
  set: ({ set }, show) => {
    set(AssetShowState, show);
    LocalCookie.set(CookieKey.SHOWASSET, show);
  },
});

/***
 * 资产折合 货币单位类型
 * 1 BTC 2 USDT
 */

export const ValuationType = atom<number>({
  key: "valuation-type-state",
  default: LocalCookie.get(CookieKey.VALUATIONUNIT),
});

export const ValuationUnitSelector = selector<string>({
  key: "valuation-unit-selector",
  get: ({ get }) => {
    let type = get(ValuationType);
    if (type === 1) {
      return "BTC";
    }
    return "USDT";
  },
});

// 暂停态标志位（取代原来的stateNumber）
export const PendingState = atom({
  key: "pending-state-public",
  default: true,
});

// 系统时间
export const SystemTimeState = atom<number>({
  key: "system-time-state-public",
  default: 0,
});

// 使用的布局信息
const LayoutState = atom<SUPPORT_LAYOUT>({
  key: "layout-state-inner",
  default: LocalCache.get<SUPPORT_LAYOUT>(CacheKey.GLOBAL_LAYOUT),
});

/**
 * 布局信息设置时需要保存到localStorage
 */
export const LayoutSelector = selector<SUPPORT_LAYOUT>({
  key: "layout-selector-public",
  get: ({ get }) => get(LayoutState),
  set: ({ set }, layout) => {
    set(LayoutState, layout);
    LocalCache.set(CacheKey.GLOBAL_LAYOUT, layout);
  },
});

export type TradeRecord = [string, string, string, number];

/**
 * 成交历史
 */
export const MixContractTradeRecordsState = atom<TradeRecord[]>({
  key: "history-list-state-inner",
  default: [],
});

export const CurrentBusinessLineState = atom<BusinessLineEnum>({
  key: "base-business-line-state",
  default: BusinessLineEnum.USDT_MIX_CONTRACT_BL,
});

/**
 * 汇率信息
 */
let defaultTrader: ratesType = {
  USDT_USD: "0",
  usdt_cny: "0",
  usdt_jpy: "0",
  usdt_krw: "0",
  usdt_twd: "0",
  usdt_rub: "0",
  usdt_eur: "0",
  usdt_try: "0",
  usdt_vnd: "0",
  usdt_IDR: "0",
  usdt_php: "0",
  usdt_hkd: "0",
  usdt_thb: "0",
};
export const tradeParameterState = atom<ratesType>({
  key: "tradeparame-state",
  default: defaultTrader,
});

/***
 * 在展示区 选择的 保证金
 * 筛选区域   仓位  当前委托
 */
export const LocalPositionFilterMargin = atom<string>({
  key: "current-show-select-margin",
  default: LocalCache.get(CacheKey.POSITION_FILTER_MARGIN) || "all",
});

/****
 * 使用set 设置本地存储
 */
export const LocalPositionFilterMarginSelector = selector<string>({
  key: "current-show-select-margin-selector",
  get: ({ get }) => {
    return get(LocalPositionFilterMargin);
  },
  set: ({ set }, margin) => {
    set(LocalPositionFilterMargin, margin);
    LocalCache.set(CacheKey.POSITION_FILTER_MARGIN, margin);
  },
});

export const TradeUnitState = atom<TradeUnitEnum>({
  key: "trade-unit-state",
  default: LocalCache.get(CacheKey.TRADE_UNIT) || TradeUnitEnum.BASE_SYMBOL,
});

/**
 * Deprecated：交易单位请使用UserPreferenceSetting中的contractUnitSet
 */
export const TradeUnitSelector = selector<TradeUnitEnum>({
  key: "trade-unit-selector",
  get: ({ get }) => {
    return get(TradeUnitState);
  },
  set: ({ set }, value) => {
    set(TradeUnitState, value);
    LocalCache.set(CacheKey.TRADE_UNIT, value);
  },
});

// ipCode
export const ipCodeState = atom<string>({
  key: "ipcode-state-public",
  default: "",
});

export const ipCodeSelector = selector<string>({
  key: "ipcode-select-public",
  get: ({ get }) => get(ipCodeState),
  set: ({ set }, code) => {
    set(ipCodeState, code);
  },
});

// 语言列表
export const languageListState = atom<LanguageTypeItem[]>({
  key: "language-list-state-public",
  default: supportLanguageList,
});

export const languageListStateSelector = selector<LanguageTypeItem[]>({
  key: "language-list-select-public",
  get: ({ get }) => get(languageListState),
  set: ({ set }, languageList) => {
    set(languageListState, languageList);
  },
});

// 路由可忽略的语言参数
export const IgnorableLocale = atom<string[]>({
  key: "ignorable-state-locale",
  default: ["en"],
});

/**
 * 设置k线周期和主图设置
 */
export const MixKlineIntervalAndStudy = atom<{
  interval: number;
  study: string;
}>({
  key: "MixKlineIntervalAndStudy",
  default: {
    interval: -1,
    study: "",
  },
});

export const DefaultFastestImageDomain = "img.cointrimg.com";
